import api from '@/services/billing';
import { Invoice } from '@/models/invoice';
import { BaseView, Filters, DataReq } from './view';
import stateColors from './state-colors';
import Frame from '@/components/Frame.vue';
import RawExpand from '@/components/Pivot/views/all/rawExpand.vue';
import ResendEmail from '@/components/Pivot/views/all/resendEmail.vue';
import Pivot from '../Pivot.vue';

import { isArray } from 'util';
import { Order } from '@/models/order';
import store from '@/store';
import { Account } from '@/models/account';

export default class IView implements BaseView {
  public Name = 'all';

  public get Headers(): any {
    return [{
      name: 'object_type',
      text: 'Type',
      value: 'object_type',
      sortable: true
    },
    // common columns
    {
      name: 'id',
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'invoiceitem', 'communicationlog'],
      text: "ID",
      value: "id",
      sortable: true,
    },
    {
      name: 'state',
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'transaction', 'communicationlog'],
      text: "State",
      value: "state",
      sortable: true,
      colors: stateColors
    },
    {
      name: 'account_id',
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'communicationlog', 'transaction'],
      text: 'Account Id',
      value: 'account_id',
      sortable: true
    },
    {
      name: 'account',
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'communicationlog', 'transaction'],
      text: 'Account',
      type: 'account',
      align: 'left',
      value: 'account_id',
      sortable: true
    },
    {
      name: 'currency',
      datasources: ['order', 'invoice', 'subscription', 'transaction', 'transaction'],
      text: 'Currency',
      value: 'currency',
      calc: (item: any, header: any) => item.currency || (item.package ? item.package.currency : ''),
      sortable: true
    },
    {
      name: 'region',
      datasources: ['order', 'invoice', 'subscription'],
      text: 'Region',
      value: 'region',
      sortable: false
    },
    {
      name: "created",
      type: "datetime",
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'communicationlog', 'transaction'],
      text: "Created",
      value: "created_at",
      sortable: true,
    },
    {
      name: "updated",
      type: "datetime",
      datasources: ['order', 'invoice', 'subscription', 'deliveryitem', 'communicationlog', 'transaction'],
      text: "Updated",
      value: "updated_at",
      sortable: true,
    },
    {
      name: 'payment_method_id',
      datasources: ['order', 'invoice', 'subscription'],
      text: 'Payment Method Id',
      value: 'payment_method_id',
      sortable: false
    },

    // common order & invoice
    {
      name: 'sales_agent',
      datasources: ['order', 'invoice'],
      text: 'Sales Agent',
      type: 'account',
      value: "sales_agent_id",
      sortable: true
    },
    {
      name: 'updated_by',
      datasources: ['order', 'invoice'],
      text: 'Updated By',
      value: 'updated_by',
      sortable: false
    },
    {
      name: 'notes',
      datasources: ['order', 'invoice'],
      text: 'Notes',
      value: 'notes',
      sortable: false
    },
    {
      name: 'lead_source',
      datasources: ['order'],
      text: 'Lead Source',
      value: 'lead_source',
      sortable: true
    },
    {
      name: 'lead_notes',
      datasources: ['order'],
      text: 'Lead Notes',
      value: 'lead_notes',
      sortable: false
    },
    {
      name: "total",
      datasources: ['order', 'invoice'],
      type: "currency",
      text: "Total",
      value: "total",
      sortable: true,
      width: '120px'
    },
    {
      name: 'reference-link',
      datasources: ['order', 'invoice', 'transaction'],
      type: 'link',
      link: (v: Order | Invoice) => {
        switch (v.object_type) {
          case 'order':
            if (v.state === 'DRAFT' || v.state === 'APPROVAL_DECLINED') {
              return `/order/${v.id}`;
            }
          case 'invoice':
            const inv: any = v;
            return inv.invoice_url_paid || inv.invoice_url;
        }
      },
      colors: { link: 'rgba(64, 209, 227, 1)', _: '#111' },
      text: "Reference",
      value: "reference",
      sortable: true,
    },

    // order subscription common columns
    {
      name: 'is_custom',
      datasources: ['order', 'subscription'],
      text: "Is Custom?",
      align: "left",
      value: "is_custom",
      sortable: false,
    },

    // invoice subscription common
    {
      name: 'account_order_id',
      datasources: ['invoice', 'subscription'],
      text: 'Account Order Id',
      value: 'account_order_id',
      sortable: false
    },
    {
      name: 'country',
      datasources: ['invoice', 'subscription', 'deliveryitem'],
      text: 'Country',
      value: 'country',
      sortable: true
    },

    // order only
    {
      name: 'exchange_rate',
      datasources: ['order'],
      text: 'Exchange Rate',
      value: 'exchange_rate',
      sortable: true
    },
    {
      name: 'billing_address',
      datasources: ['order'],
      type: 'address',
      align: 'left',
      text: 'Billing Address',
      value: 'billing_address',
      sortable: false
    },
    {
      name: 'delivery_address',
      datasources: ['order'],
      type: 'address',
      align: 'left',
      text: 'Delivery Address',
      value: 'delivery_address',
      sortable: false
    },
    {
      name: 'packages',
      datasources: ['order'],
      text: 'Packages',
      align: 'left',
      type: 'package-list',
      value: 'packages',
      sortable: false
    },
    {
      name: 'contact_details',
      datasources: ['order'],
      text: 'Contact Details',
      align: 'left',
      type: 'contact_details',
      value: 'contact_details',
      sortable: false
    },
    {
      name: 'company_details',
      datasources: ['order'],
      text: 'Company Details',
      align: 'left',
      type: 'company_details',
      value: 'company_details',
      sortable: false
    },
    {
      name: 'friends',
      datasources: ['order'],
      type: 'friends_list',
      text: 'Friends',
      value: 'friends',
      sortable: false
    },
    {
      name: 'release_without_payment',
      datasources: ['order'],
      text: 'Release Without Payment',
      value: 'release_without_payment',
      sortable: false
    },

    // invoice only fields
    {
      name: 'invoice_url',
      datasources: ['invoice'],
      text: 'Invoice Url',
      type: 'link',
      link: (v: Invoice) => {
        return v.invoice_url;
      },
      colors: { _: 'rgba(64, 209, 227, 1)' },
      value: 'invoice_url',
      sortable: false
    },
    {
      name: 'invoice_url_paid',
      datasources: ['invoice'],
      text: 'Invoice Url Paid',
      type: 'link',
      link: (v: Invoice) => {
        return v.invoice_url_paid;
      },
      colors: { _: 'rgba(64, 209, 227, 1)' },
      value: 'invoice_url_paid',
      sortable: false
    },
    {
      name: 'due_date',
      datasources: ['invoice'],
      type: "datetime",
      text: "Due Date",
      value: "due",
      sortable: true
    },
    {
      name: 'discount',
      datasources: ['invoice'],
      text: 'Discount',
      value: 'discount',
      sortable: true
    },
    {
      name: "tax",
      datasources: ['invoice'],
      type: "currency",
      text: "Tax",
      value: "tax",
      width: '120px',
      sortable: true,
    },
    {
      name: 'next_payment_attempt',
      datasources: ['invoice'],
      type: "datetime",
      text: "Next Payment Attempt",
      value: "next_payment_attempt",
      sortable: true
    },
    {
      name: 'payment_attempts',
      datasources: ['invoice'],
      text: 'Payment Attempts',
      value: 'payment_attempts',
      sortable: true
    },

    // subscription only fields
    {
      name: 'package_id',
      datasources: ['subscription', 'deliveryitem', 'deliveryitem', 'invoiceitem'],
      text: 'Package Id',
      align: 'center',
      value: 'package_id',
      sortable: false
    },
    {
      name: 'code',
      datasources: ['subscription', 'deliveryitem', 'invoiceitem'],
      text: "Code",
      align: "left",
      value: "code",
      calc: (item: any, header: any) => item.code || (item.package ? item.package.code : ''),
      sortable: true,
    },
    {
      name: 'qty',
      datasources: ['subscription', 'deliveryitem', 'invoiceitem'],
      text: 'Qty',
      align: 'center',
      value: 'qty',
      sortable: true
    },
    {
      name: 'interval',
      datasources: ['subscription', 'invoiceitem'],
      text: 'Interval',
      type: 'friendly_interval',
      align: 'center',
      value: 'interval',
      calc: (item: any, header: any) => item.interval || (item.subscription ? item.subscription.interval : ''),
      sortable: true
    },
    {
      name: 'prev_renewal',
      datasources: ['subscription'],
      type: "datetime",
      text: "Prev Renewal",
      align: "left",
      value: "prev_renewal",
      calc: (item: any, header: any) => item.prev_renewal || (item.subscription ? item.subscription.prev_renewal : ''),
      sortable: true
    },
    {
      name: 'next_renewal',
      datasources: ['subscription'],
      type: "datetime",
      text: "Next Renewal",
      align: "left",
      value: "next_renewal",
      calc: (item: any, header: any) => item.next_renewal || (item.subscription ? item.subscription.next_renewal : ''),
      sortable: true
    },
    {
      name: 'expiry',
      datasources: ['subscription'],
      type: "datetime",
      text: "Expiry",
      align: "left",
      value: "expiry",
      calc: (item: any, header: any) => item.expiry || (item.subscription ? item.subscription.expiry : ''),
      sortable: true
    },

    // invoiceitem
    {
      name: 'account_invoice_id',
      datasources: ['invoiceitem'],
      text: "Invoice ID",
      align: "left",
      value: "account_invoice_id",
      sortable: true
    },
    {
      name: 'account_delivery_item_id',
      datasources: ['invoiceitem'],
      text: "Delivery Item ID",
      align: "left",
      value: "account_delivery_item_id",
      sortable: true
    },
    {
      name: 'account_subscription_id',
      datasources: ['invoiceitem'],
      text: "Subscription ID",
      align: "left",
      value: "account_subscription_id",
      sortable: true
    },
    {
      name: 'order_coupon_id',
      datasources: ['invoiceitem'],
      text: "Coupon ID",
      align: "left",
      value: "order_coupon_id",
      sortable: true
    },
    {
      name: 'price',
      type: 'currency',
      datasources: ['invoiceitem'],
      text: "Price",
      align: "left",
      value: "price",
      sortable: true
    },
    {
      name: 'discount_rate',
      datasources: ['invoiceitem'],
      text: 'Discount',
      value: 'discount_rate',
      sortable: true
    },

    // package fields

    {
      name: 'account_code',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: "Account Code",
      value: "account_code",
      calc: (item: any, header: any) => item.account_code || (item.package ? item.package.account_code : ''),
      sortable: false,
    },
    {
      name: 'title',
      text: "Title",
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      align: 'left',
      value: "title",
      calc: (item: any, header: any) => item.title || (item.package ? item.package.title : ''),
      sortable: false,
    },
    {
      name: 'title-icon',
      text: "Title",
      type: "title-icon",
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      align: 'left',
      value: "title",
      calc: (item: any, header: any) => item.title || (item.package ? item.package.title : ''),
      sortable: false,
    },
    {
      name: 'description',
      text: "Description",
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      align: 'left',
      value: "description",
      calc: (item: any, header: any) => item.description || (item.package ? item.package.description : ''),
      sortable: false,
      width: '220px'
    },
    {
      name: 'category',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: "Category",
      value: "category",
      calc: (item: any, header: any) => item.category || (item.package ? item.package.category : ''),
      sortable: false,
      width: '220px'
    },
    {
      name: 'long_description',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: "Long Description",
      value: "long_description",
      calc: (item: any, header: any) => item.long_description || (item.package ? item.package.long_description : ''),
      sortable: false,
    },
    {
      name: 'is_plan',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: 'Is Plan',
      value: 'is_plan',
      calc: (item: any, header: any) => item.is_plan || (item.package ? item.package.is_plan : ''),
      sortable: false
    },
    {
      name: 'allow_multiple',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: 'Allow Multiple',
      value: 'allow_multiple',
      calc: (item: any, header: any) => item.allow_multiple || (item.package ? item.package.allow_multiple : ''),
      sortable: false
    },
    {
      name: 'sku',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: 'Sku',
      value: 'sku',
      calc: (item: any, header: any) => item.sku || (item.package ? item.package.sku : ''),
      sortable: false
    },
    {
      name: 'version',
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: 'Version',
      value: 'version',
      calc: (item: any, header: any) => item.version || (item.package ? item.package.version : ''),
      sortable: false
    },
    {
      name: "expiry",
      type: "datetime",
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: "Expiry",
      value: "expiry",
      calc: (item: any, header: any) => item.expiry || (item.package ? item.package.expiry : ''),
      sortable: false,
    },
    {
      name: "available_from",
      type: "datetime",
      datasources: ['invoiceitem', 'package', 'deliveryitem'],
      text: "Available From",
      value: "available_from",
      calc: (item: any, header: any) => item.available_from || (item.package ? item.package.available_from : ''),
      sortable: false,
    },

    // expanding columns
    {
      name: 'expand_invoice_or_packages',
      datasources: ['invoice', 'order'],
      type: "more",
      text: "More",
      value: "id",
      sortable: false,
      width: 10,
      expand: {
        name: 'expand-packages-invoice',
        // component: (row: Order | Invoice) => row.object_type === 'order' ? Pivot : Frame,
        component: (row: any) => Pivot,
        props: (row: any) => { // Order | Invoice) => {
          switch (row.object_type) {
            case 'order':
              return {
                BaseViewName: "package",
                ViewName: "Package Expand",
                StaticData: row.packages
              };
              break;
            case 'invoice':
              return {
                BaseViewName: "all",
                ViewName: "Invoice Expand",
                DatasourceFilters: {
                  account_invoice_id: [row.id]
                }
              };
              break;
            default:
              break;
          }
        }
      }
    },
    {
      name: 'expand_packages',
      datasources: ['order'],
      type: "more",
      text: "Packages",
      value: "id",
      sortable: false,
      width: '10px',
      expand: {
        name: 'expand-order',
        component: (row: any) => Pivot,
        props: (row: Order) => ({
          BaseViewName: "package",
          ViewName: "Package Expand",
          StaticData: row.packages
        })
      }
    },
    {
      name: 'expand_invoice',
      datasources: ['invoice'],
      type: "more",
      text: "Invoice",
      value: "id",
      sortable: false,
      width: 10,
      expand: (item: Invoice) => {
        return {
          name: 'expand-invoice',
          component: (row: any) => Frame,
          props: (row: Invoice) => ({
            value: row.invoice_url
          })
        };
      }
    },
    {
      name: 'expand_raw',
      type: "more",
      text: "Raw Expand",
      value: "id",
      sortable: false,
      width: 10,
      expand: {
        name: 'expand-raw',
        component: (row: any) => RawExpand,
        props: (row: any) => ({
          value: row
        })
      }
    },

    // communication logs
    {
      name: 'email',
      datasources: ['communicationlog'],
      text: 'Email',
      value: 'email',
      sortable: false
    },
    {
      name: 'resend',
      type: 'custom',
      datasources: ['communicationlog'],
      text: 'Resend',
      value: 'id',
      sortable: false,
      component: (row: any) => ResendEmail,
      props: (row: any) => ({
        value: row
      })
    },
    {
      name: 'subject',
      datasources: ['communicationlog'],
      text: 'Subject',
      value: 'subject',
      sortable: false
    },
    {
      name: 'template_id',
      datasources: ['communicationlog'],
      text: 'TemplateID',
      value: 'template_id',
      sortable: false
    },
    {
      name: 'service',
      datasources: ['communicationlog', 'transaction'],
      text: 'Service',
      value: 'service',
      sortable: false
    },
    {
      name: 'status',
      datasources: ['communicationlog'],
      text: "Status (depricated)",
      value: "status",
      sortable: true,
      colors: stateColors
    },
    {
      name: 'type',
      datasources: ['transaction'],
      text: "Type",
      value: "type",
      sortable: true,
    },
    {
      name: 'sub_type',
      datasources: ['transaction'],
      text: "Sub Type",
      value: "sub_type",
      sortable: true,
    },
    {
      name: "amount",
      datasources: ['transaction'],
      type: "currency",
      text: "Amount",
      value: "amount",
      sortable: true,
      width: '120px'
    },
    ];
  }

  private actions = {
    edit: {
      icon: 'edit',
      link: (v: Invoice) => {
        return `/invoice/${v.id}`;
      }
    },
    view: {
      icon: 'visibility',
      link: (v: Invoice) => {
        return;
      }
    },
    mail: {
      icon: 'mail',
      link: (v: Invoice) => {
        return;
      }
    }
  };
  public async Data(req: DataReq) {
    if (!req.sources) {
      req.sources = ['order'];
    }
    const data = await api.ListPivotAll(req);
    const res: any = [];
    for (const d of data) {
      d.ob.object_type = d.object_type;
      res.push(d.ob);
    }
    return res;
  }

  public Filter(v: any) {
    return true;
  }

  public ValueStyle(header: any, value: any, item: any) {
    const style: any = {};
    if (header.colors) {
      if (header.type === 'link' && header.link && header.colors.link) {
        if (header.link(item)) {
          style.color = header.colors.link;
        }
      } else {
        style.color = header.colors[value] ? header.colors[value] : header.colors._ || "#000";
      }
    }
    return style;
  }

  public ItemActions(v: Invoice) {
    const actions: any = [
      // this.actions.view
    ];

    const OActions: any = {
      DRAFT: [this.actions.edit],
      APPROVAL_DECLINED: [this.actions.edit],
      APPROVED: [this.actions.mail],
      CANCELED: [this.actions.mail]
    };

    if (v.state && OActions[v.state]) {
      actions.push(...OActions[v.state]);
    }

    return actions;
  }

  public get DatasourceFilters() {
    const filters = [
      {
        name: 'date_range',
        text: 'Date Range',
        required: true,
        type: 'date-range',
        value: (data: any) => undefined
      },
      {
        name: 'sources',
        text: 'Source',
        required: true,
        type: 'data-sources',
        multiple: true,
        items: () => [
          { val: "order", text: "Orders" },
          { val: "invoice", text: "Invoices" },
          { val: "subscription", text: "Subscriptions" },
          { val: "deliveryitem", text: "Delivery Item" },
          { val: "invoiceitem", text: "Invoice Item" },
          { val: "communicationlog", text: "Communication Log" },
          { val: "transaction", text: "Transactions" },
        ],
        selectMultiple: true,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return selected;
          }
          return [selected];
        }
      },
      {
        name: 'account_id',
        text: 'Account',
        required: false,
        type: 'account',
        multiple: true,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return (selected as Account[]).map((a) => a.id);
          }
          return [selected.id];
        }
      },
      {
        name: 'interval',
        text: 'Interval',
        required: false,
        type: 'multi',
        multiple: true,
        items: () => [
          { val: "month", text: "Month" },
          { val: "year", text: "Year" }
        ],
        selectMultiple: false,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return selected;
          }
          return [selected];
        }
      },
      {
        name: 'state',
        text: 'State',
        required: false,
        type: 'multi',
        multiple: true,
        items: () => [
          // Common
          { val: 'DRAFT', text: 'Draft (O,I,S)' },

          // order & invoice
          { val: 'CREATED', text: 'Created (O,I)' },
          { val: 'PAID', text: 'Paid (O,I)' },

          // invoice & subscription
          { val: 'PENDING_PAYMENT', text: 'Pending Payment (O,S)' },

          // order
          { val: "PENDING_APPROVAL", text: "Pending Approval (O)" },
          { val: "APPROVAL_DECLINED", text: "Approval Declined (O)" },
          { val: "APPROVED", text: "Approved (O)" },

          // invoice
          { val: 'PAYING', text: 'Paying (I)' },
          { val: 'PAYMENT_FAILED', text: 'Payment Failed (I)' },
          { val: 'PAYMENT_RETRY', text: 'Payment Retry (I)' },
          { val: 'FINAL_PAYMENT_ATTEMPT_FAILED', text: 'Final Payment Attempt Failed (I)' },
          { val: 'CANCELED', text: 'Canceled (I)' },

          // subscription
          { val: 'ACTIVE', text: 'Active (S)' },
          { val: 'RENEWING', text: 'Renewing (S)' },
          { val: 'PENDING_CANCEL', text: 'Pending Cancel (S)' },
          { val: 'CANCELLED', text: 'Cancelled (S)' },
          // { val: 'PENDING_TRIAL_CREATE', text: 'Pending Trial Create (S)' },
        ],
        selectMultiple: false,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return selected;
          }
          return [selected];
        }
      },
      {
        name: 'currency',
        text: 'Currency',
        required: false,
        type: 'multi',
        multiple: true,
        items: () => [
          { val: "ZAR", text: "ZAR" },
          { val: "USD", text: "USD" }
        ],
        selectMultiple: false,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return selected;
          }
          return [selected];
        }
      },
      {
        name: 'reference',
        text: 'Reference',
        type: 'string',
        required: false,
        selectMultiple: false,
        value: (str: string) => {
          return str;
        }
      },
      {
        name: 'sales_agent_id',
        text: 'Sales Agents',
        required: false,
        type: 'sales_agent',
        multiple: true,
        selectMultiple: false,
        value: (selected: any) => {
          if (!selected) { return null; }
          if (isArray(selected)) {
            if (!selected.length) { return null; }
            return selected;
          }
          return [selected];
        }
      }
    ];

    // // string filters
    // const stringFilters = [
    //   { name: 'account_code', text: 'Xero Account Code' },
    //   { name: 'category', text: 'Category' },
    //   { name: 'code', text: 'Code' },
    //   { name: 'country', text: 'Country' },
    //   { name: 'description', text: 'Description' },
    //   { name: 'reference', text: 'Reference' },
    //   { name: 'region', text: 'Region' },
    //   { name: 'title', text: 'Title' }]
    // for (let f of stringFilters) {
    //   filters.push({
    //     name: f.name,
    //     text: f.text,
    //     required: false,
    //     type: 'string',
    //     multiple: false,
    //     selectMultiple: false,
    //     value: (selected: any) => {
    //       if (!selected) { return null; }
    //       if (isArray(selected)) {
    //         if (!selected.length) { return null; }
    //         return selected;
    //       }
    //       return [selected];
    //     }
    //   })
    // }

    // // number filters
    // const numberFilters = [
    //   { name: "discount", text: "Discount" },
    //   { name: "discount_rate", text: "Discount Rate" },
    //   { name: "sku", text: "Sku" },
    //   { name: "tax", text: "Tax" },
    //   { name: "price", text: "Price" },
    //   { name: "qty", text: "Qty" },
    //   { name: "total", text: "Total" },
    //   { name: "exchange_rate", text: "Exchange Rate" },
    //   { name: "payment_attempts", text: "Payment Attempts" }]
    // for (let f of numberFilters) {
    //   filters.push({
    //     name: f.name,
    //     text: f.text,
    //     required: false,
    //     type: 'number',
    //     multiple: false,
    //     selectMultiple: false,
    //     value: (selected: any) => {
    //       if (!selected) { return null; }
    //       if (isArray(selected)) {
    //         if (!selected.length) { return null; }
    //         return selected;
    //       }
    //       return [selected];
    //     }
    //   })
    // }

    return filters;
  }

  public get Actions(): any {
    const actions: any[] = [];
    if (store.getters["auth/hasPermission"]('accounts.can_write')) {
      actions.push({
        name: 'new-order',
        text: 'New Order',
        type: 'btn',
        icon: 'add',
        action: () => {
          store.dispatch('newOrder');
        }
      });
    }
    return actions;
  }
}
